import React from 'react';
import { CloseButton, ElementProps, TextInput, TextInputProps } from '@mantine/core';
import { useDebouncedCallback, useMergedRef } from '@mantine/hooks';
import { EMPTY_DISPLAY_PLACEHOLDER, toMantineError } from '../../theme/forms';
import { describedByContainer } from '../../theme/describedByContainer';
import { useFieldA11y } from '../../hooks/useFieldA11y';
import type { InputMaskPattern } from './inputMask';
import { useInputMask } from './useInputMask';
import './Input.scss';

// Фасад на Mantine TextInput. Input — КОРЕНЬ семейства (InputNumber/InputPhone наследуют
// IInputProps и оборачивают <Input>, InputControl спредит произвольные пропы), поэтому тип
// не узкий как у InputPassword, а сохраняет обратносовместимое ЯДРО легаси-фич:
// pattern-фильтр, mask, onClear, debounce, controlled+uncontrolled.
// Глобальная тема полей (styles/_mantineFields.scss) даёт паритет вида с .rf-input. См. API-GUIDELINES.
export interface IInputProps
    extends
        Omit<TextInputProps, 'error' | 'size' | 'leftSection' | 'rightSection' | 'onChange' | 'value'>,
        ElementProps<'input', 'size' | 'onChange' | 'value' | 'defaultValue' | 'pattern'> {
    /** Ссылка на input-элемент */
    ref?: React.Ref<HTMLInputElement>;
    /** Размер по шкале дизайн-системы @default 'sm' */
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /** Нативный onChange (event) — фасад перехватывает (pattern-фильтр) и пробрасывает. */
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    /** Контролируемое значение; null из форм нормализуется в пустую строку. */
    value?: TextInputProps['value'] | null;
    /** Невалидное состояние (мост invalid→error через toMantineError, см. API-GUIDELINES §5). */
    invalid?: boolean;
    /**
     * Значение строкой в тот же момент, что `onChange` (каждый ввод, прошедший `pattern`), и `''` при сбросе
     * крестиком. Фиксация по уходу из поля — `onBlur`. Тип value намеренно широкий: наследники (`InputNumber`,
     * `InputControl`) эмитят string|number|null (легаси-контракт; узкая типизация — на их миграциях).
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onValueChange?: (value: any) => void;
    /**
     * Маска ввода: `9` — цифра, `a` — латинская буква, `*` — буква или цифра, `\` экранирует символ, остальное
     * постоянно (`+7 999 999-99-99`); массив — постоянные символы строками, позиции ввода RegExp. Поле с маской
     * неуправляемое для React: `value` приходит в него императивно, ввод — через `onChange`/`onValueChange`
     * с текстом по маске (с символами незаполненных позиций, пока поле в фокусе).
     */
    mask?: InputMaskPattern;
    /** Символ незаполненной позиции маски, `null` — не показывать позиции @default '_' */
    maskPlaceholder?: string | null;
    /**
     * Регэксп-ФИЛЬТР ввода (строка-паттерн, НЕ нативный HTML pattern): символ отклоняется,
     * если new RegExp(pattern).test(value) === false. @example '^[\\da-zA-Zа-яА-Я]*$'
     */
    pattern?: string;
    /** Кнопка очистки (CloseButton в rightSection при непустом value). */
    onClear?: () => void;
    /** Дебаунс-задержка для onDebouncedChange, мс @default 300 */
    debounce?: number;
    /** Значение после паузы ввода (не на первом рендере); сброс крестиком — сразу `''`. */
    onDebouncedChange?: (value: string) => void;
    /** Контент в начало (codemod: startAdornment → leftSection). */
    leftSection?: React.ReactNode;
    /** Контент в конец (codemod: endAdornment / icon → rightSection). */
    rightSection?: React.ReactNode;
    /**
     * @deprecated Легаси-testid: рендерит data-testid="input-${testid}". Уйдёт в едином
     * кросс-проектном testid→data-testid пассе (см. API-GUIDELINES). Пока сохранён для e2e.
     */
    testid?: string;
    /** Показывать счётчик символов для настроенного maxLength. */
    showMaxLengthCounter?: boolean;
}

const COUNTER_BELOW_ORDER: TextInputProps['inputWrapperOrder'] = ['label', 'input', 'description', 'error'];

const Input: React.FC<IInputProps> = ({
    ref,
    invalid,
    onChange,
    onValueChange,
    onFocus,
    onBlur,
    mask,
    maskPlaceholder = '_',
    pattern,
    onClear,
    debounce = 300,
    onDebouncedChange,
    leftSection,
    rightSection,
    size = 'sm',
    value,
    defaultValue,
    readOnly,
    disabled,
    autoFocus,
    testid,
    showMaxLengthCounter = false,
    maxLength,
    description,
    placeholder,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    inputContainer,
    ...rest
}) => {
    // Подпись и сообщение группы (FormGroup): своя подпись Mantine (`label`) важнее подписи группы.
    const fieldA11y = useFieldA11y(
        { 'aria-label': ariaLabel, 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy },
        !!rest.label,
    );
    // readOnly НЕ триггерит прочерк: его выставляет и Dictpicker на свой ИНТЕРАКТИВНЫЙ
    // триггер (в пикере не печатают — открывают справочник), там приглашение уместно.
    const resolvedPlaceholder = disabled ? EMPTY_DISPLAY_PLACEHOLDER : placeholder;
    const normalizedValue = value === null ? '' : value;
    const regexp = React.useMemo(() => (pattern ? new RegExp(pattern) : null), [pattern]);

    // useDebouncedCallback (@mantine/hooks) не стреляет на маунте — паритет со старым
    // useDebounce (isMounted-guard). Имеет .cancel() (используем при clear).
    const debouncedFire = useDebouncedCallback((v: string) => onDebouncedChange?.(v), debounce);

    // Внешний ref + autoFocus({preventScroll}) — Mantine приземляет ref на нативный input.
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    const lastValidRef = React.useRef<string>(
        value != null ? String(value) : defaultValue != null ? String(defaultValue) : '',
    );

    const emitChange = (v: string, event: React.ChangeEvent<HTMLInputElement>): boolean => {
        if (regexp && !regexp.test(v)) return false;
        lastValidRef.current = v;
        onChange?.(event);
        onValueChange?.(v);
        if (onDebouncedChange) debouncedFire(v);
        return true;
    };

    const maskField = useInputMask({
        mask,
        slotChar: maskPlaceholder,
        editable: !readOnly && !disabled,
        invalid,
        value: normalizedValue == null ? undefined : String(normalizedValue),
        defaultValue: defaultValue == null ? undefined : String(defaultValue),
        onInput: (text, input) => emitChange(text, maskChangeEvent(input)),
    });
    const mergedRef = useMergedRef(ref, innerRef, maskField.ref);

    React.useEffect(() => {
        if (autoFocus && innerRef.current) {
            innerRef.current.focus({ preventScroll: true });
        }
    }, [autoFocus]);

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        if (readOnly) return;
        if (maskField.active) {
            maskField.applyText(e.currentTarget.value);
            return;
        }
        // Паритет с легаси-фильтром; работает и в uncontrolled (Mantine держит value сам) —
        // откатываем DOM к последнему валидному значению, onChange не зовём.
        if (!emitChange(e.currentTarget.value, e)) e.currentTarget.value = lastValidRef.current;
    };

    // readOnly подавляет focus/blur.
    const handleBlur: React.FocusEventHandler<HTMLInputElement> = (e) => {
        if (readOnly) return;
        onBlur?.(e);
    };

    const handleFocus: React.FocusEventHandler<HTMLInputElement> = (e) => {
        if (readOnly) return;
        onFocus?.(e);
    };

    // overflow-tooltip (легаси onMouseOver): title=value при переполнении.
    const handleMouseOver: React.MouseEventHandler<HTMLInputElement> = (e) => {
        const el = e.currentTarget;
        if (el.clientWidth < el.scrollWidth || el.clientHeight < el.scrollHeight) {
            el.setAttribute('title', el.value);
        } else {
            el.removeAttribute('title');
        }
    };

    // onClear → CloseButton в rightSection при непустом value (паритет clearInput).
    const valueStr = value == null ? '' : String(value);
    const showCounter = showMaxLengthCounter && maxLength !== undefined && maxLength > 0;
    const maxLengthDescription = showCounter ? (
        <span className="rf-input__max-length">
            Осталось символов: {maxLength - valueStr.length}/{maxLength}
        </span>
    ) : (
        description
    );
    const showClear = !!onClear && valueStr.length > 0 && !readOnly && !disabled;
    const clearBtn = showClear ? (
        <CloseButton
            size="sm"
            aria-label="Сбросить"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
                onValueChange?.('');
                debouncedFire.cancel();
                onDebouncedChange?.('');
                onClear?.();
            }}
        />
    ) : null;

    const common = {
        ...rest,
        'aria-label': ariaLabel,
        'aria-labelledby': fieldA11y['aria-labelledby'],
        inputContainer: describedByContainer(fieldA11y['aria-describedby'], inputContainer),
        ref: mergedRef,
        ...(maskField.active ? { defaultValue: maskField.initialText } : { value: normalizedValue, defaultValue }),
        size,
        readOnly,
        disabled,
        maxLength,
        description: maxLengthDescription,
        // Счётчик под полем (как у Textarea): над полем он сдвигает инпут вниз относительно соседей в ряду.
        ...(showCounter ? { inputWrapperOrder: COUNTER_BELOW_ORDER } : {}),
        placeholder: resolvedPlaceholder,
        error: toMantineError(invalid),
        leftSection,
        rightSection: clearBtn ?? rightSection,
        rightSectionPointerEvents: clearBtn ? 'auto' : undefined,
        onChange: handleChange,
        onBlur: handleBlur,
        onFocus: handleFocus,
        onMouseOver: handleMouseOver,
        ...(testid ? { 'data-testid': `input-${testid}` } : {}),
    } satisfies Record<string, unknown>;

    return <TextInput {...(common as TextInputProps)} />;
};

const maskChangeEvent = (input: HTMLInputElement) =>
    ({ target: input, currentTarget: input, type: 'change' }) as unknown as React.ChangeEvent<HTMLInputElement>;

export default Input;
